import React from 'react';
import { useQuery } from 'react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import api from '../services/api';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

function Statistics() {
  const { data, isLoading } = useQuery('stats', async () => {
    const response = await api.get('/stats');
    return response.data;
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const byService = data?.byService || [];

  // Datos para gráficos
  const statusData = [
    { name: 'Activas', value: stats.activeCookies || 0 },
    { name: 'Usadas', value: stats.usedCookies || 0 },
    { name: 'Expiradas', value: stats.expiredCookies || 0 },
    { name: 'Inválidas', value: stats.invalidCookies || 0 }
  ].filter(item => item.value > 0);

  const amountData = [
    { name: 'Total', amount: stats.totalAmount || 0 },
    { name: 'Pagado', amount: stats.paidAmount || 0 },
    { name: 'Pendiente', amount: stats.pendingAmount || 0 }
  ];

  const serviceData = byService.map(s => ({
    name: s._id,
    value: s.count,
    amount: s.totalAmount
  }));

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Estadísticas</h2>

        {/* Cards de resumen */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 truncate">Total Cookies</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">{stats.totalCookies || 0}</dd>
              <dd className="mt-2 text-sm text-gray-600">
                {stats.activeCookies || 0} activas · {stats.usedCookies || 0} usadas
              </dd>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 truncate">Monto Total</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">${(stats.totalAmount || 0).toFixed(2)}</dd>
              <dd className="mt-2 text-sm text-gray-600">
                Pagado: ${(stats.paidAmount || 0).toFixed(2)}
              </dd>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 truncate">Tasa de Éxito</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">
                {stats.totalCookies > 0 
                  ? Math.round((stats.usedCookies / stats.totalCookies) * 100) 
                  : 0}%
              </dd>
              <dd className="mt-2 text-sm text-gray-600">
                {stats.usedCookies || 0} de {stats.totalCookies || 0} usadas
              </dd>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 truncate">Cookies Válidas</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">
                {stats.totalCookies - (stats.invalidCookies || 0)}/{stats.totalCookies || 0}
              </dd>
              <dd className="mt-2 text-sm text-gray-600">
                {stats.invalidCookies || 0} inválidas
              </dd>
            </div>
          </div>
        </div>

        {/* Gráficos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Estado de Cookies */}
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Estado de Cookies</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Montos */}
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Montos (USD)</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={amountData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="amount" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Distribución por Servicio */}
          {serviceData.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200 lg:col-span-2">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Distribución por Servicio</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={serviceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="left" orientation="left" stroke="#3B82F6" />
                    <YAxis yAxisId="right" orientation="right" stroke="#10B981" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="value" fill="#3B82F6" name="Cantidad" />
                    <Bar yAxisId="right" dataKey="amount" fill="#10B981" name="Monto Total" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* Tabla de servicios */}
        {byService.length > 0 && (
          <div className="mt-8 bg-white shadow rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Detalle por Servicio</h3>
            </div>
            <div className="p-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Servicio</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activas</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {byService.map((service) => (
                    <tr key={service._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 capitalize">
                        {service._id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {service.count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        ${service.totalAmount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {service.active}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Statistics;